import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../config/api';

interface Material {
    id: string;
    name: string;
    description: string;
    unitPrice: number;
    quantity: number;
    metadata?: {
        [key: string]: string;
    };
    Metadata?: {
        [key: string]: string;
    };
    categoryId: number;
    categoryName: string;
    active: boolean;
    inventory: boolean | string;
    stockLimit: number;
    measurementUnit: string;
    width: number;
    height: number;
    length: number;
}

interface Price {
    id: string;
    name: string;
    description: string;
    unitPrice: number;
    quantity: number;
}

interface LegPrice {
    id: string;
    name: string;
    description: string;
    unitPrice: number;
    quantity: number;
}

interface MaterialsState {
    items: Material[];
    price: Price[];
    pagination: any;
    legPrice: LegPrice[];
    material: Material | null;
    categories: any[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    listStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    detailsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    saveStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: ErrorType | null;
}

interface ErrorType {
    message: string;
}

interface materialsPayload {
    name: string,
    description: string,
    unitPrice: number,
    defaultPrice: number,
    measurementUnit: string,
    categoryId: number,
    width: number,
    height: number,
    length: number,
    prices: {
        OR: number,
        NV: number
    },
    metadata: {
        [key: string]: string;

    };
    quantity: number;
    stockLimit: number;
    active: boolean;
}

interface materialsUpdatePayload {
    id: number,
    name: string,
    description: string,
    unitPrice: number,
    defaultPrice: number,
    measurementUnit: string,
    categoryId: number,
    width: number,
    height: number,
    length: number,
    prices: {
        OR: number,
        NV: number
    },
    metadata: {
        [key: string]: string;

    };
    quantity: number;
    stockLimit: number;
    active: boolean;
    inventory: boolean;
}

interface FetchMaterialsArgs {
    pageSize: number;
    page: number;
    sortOrder: string;
    name?: string;
    description?: string;
    categoryId?: number;
    width?: number;
    height?: number;
    length?: number;
}

export const fetchMaterials = createAsyncThunk(
    'materials/fetchMaterials',
    async ({ pageSize, page = 1, sortOrder = 'DESC', name, description, categoryId, width, height, length }: FetchMaterialsArgs, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = { Authorization: `Bearer ${token}` };
            let query = `/Materials?pageSize=${pageSize}&page=${page}&sortOrder=${sortOrder}`;
            if (name) query += `&name=${name}`;
            if (description) query += `&description=${description}`;
            if (categoryId) query += `&categoryId=${categoryId}`;
            if (width) query += `&width=${width}`;
            if (height) query += `&height=${height}`;
            if (length) query += `&length=${length}`;
            const response = await api.get(query, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

interface FetchMaterialsPriceArgs {
    pageSize: number;
    page: number;
    sortOrder: string;
    name?: string;
    description?: string;
    categoryId?: number;
    width?: number;
    height?: number;
    length?: number;
}




export const fetchMaterialsPrice = createAsyncThunk(
    'materials/fetchMaterialsPrice',
    async ({ pageSize, page = 1, sortOrder = 'DESC', name, description, categoryId, width, height, length }:

        FetchMaterialsArgs, { rejectWithValue, getState }) => {
        console.log("fetchMaterialsPrice called with:", { pageSize, page, sortOrder, name, description, categoryId, width, height, length });
        try {
            const token = (getState() as any).auth.token;
            const headers = { Authorization: `Bearer ${token}` };
            let query = `/Materials?pageSize=${pageSize}&page=${page}&sortOrder=${sortOrder}`;
            if (name) query += `&name=${name}`;
            if (description) query += `&description=${description}`;
            if (categoryId) query += `&categoryId=${categoryId}`;
            if (width) query += `&width=${width}`;
            if (height) query += `&height=${height}`;
            if (length) query += `&length=${length}`;
            const response = await api.get(query, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);



export const fetchLegsPrice = createAsyncThunk(
    'materials/fetchLegsPrice',
    async ({ categoryId, height, length, width, pageSize = 1000, page = 1, sortOrder = 'ASC' }: FetchMaterialsPriceArgs, { getState, rejectWithValue }) => {
        if (!categoryId || !height || !length) {
            return rejectWithValue('Missing parameters for fetching materials');
        }
        const token = (getState() as any).auth.token;

        if (!token) {
            return rejectWithValue('No authentication token available');
        }

        const headers = { Authorization: `Bearer ${token}` };
        let query = `/Materials?pageSize=${pageSize}&page=${page}&sortOrder=${sortOrder}&categoryId=${categoryId}&height=${height}&length=${length}`;

        if (width) {
            query += `&width=${width}`;
        }

        try {
            const response = await api.get(query, { headers });
            return response.data;
        } catch (err: any) {

            return rejectWithValue(err.response.data);
        }
    }
);




const normalizeKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map((v) => normalizeKeys(v));
    } else if (obj !== null && obj.constructor === Object) {
        return Object.keys(obj).reduce((result, key) => {
            const normalizedKey = key.charAt(0).toLowerCase() + key.slice(1);
            result[normalizedKey] = normalizeKeys(obj[key]);
            return result;
        }, {} as any);
    }
    return obj;
};

export const fetchMaterialById = createAsyncThunk(
    'materials/fetchMaterialById',
    async (id, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = {
                Authorization: `Bearer ${token}`
            };
            const response = await api.get(`/Materials/${id}`, { headers });
            return normalizeKeys(response.data);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const createMaterial = createAsyncThunk(
    'materials/createMaterial',
    async (materialData: materialsPayload, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            };
            const response = await api.post('/Materials', materialData, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);


export const updateMaterial = createAsyncThunk(
    'materials/updateMaterial',
    async (materialData: materialsUpdatePayload, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            };
            const response = await api.put(`/Materials/${materialData.id}`, materialData, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

//  deleteMaterial
export const deleteMaterial = createAsyncThunk(
    'materials/deleteMaterial',
    async (id: string, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = {
                Authorization: `Bearer ${token}`
            };
            const response = await api.delete(`/Materials/${id}`, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);


//  Import and export in bulk 

export const exportMaterials = createAsyncThunk(
    'materials/exportMaterials',
    async (categoryId: { CategoryId: number }, { getState, rejectWithValue }) => {
        const token = (getState() as any).auth.token;
        const headers = { Authorization: `Bearer ${token}` };
        try {
            const response = await api.post('/Materials/Export', categoryId, { headers });
            return response.data; // Assuming the API returns a URL or blob for the file
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const importMaterials = createAsyncThunk(
    'materials/importMaterials',
    async (file: File, { getState, rejectWithValue }) => {
        const token = (getState() as any).auth.token;
        const headers = { Authorization: `Bearer ${token}` };
        const formData = new FormData();
        formData.append("file", file);
        try {
            const response = await api.post('/Materials/Import', formData, { headers });
            return response.data; // Handle response accordingly
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Categories 


export const fetchCategories = createAsyncThunk(
    'materials/fetchCategories',
    async (_, { rejectWithValue, getState }) => {
        const token = (getState() as any).auth.token;
        const pageSize = 100;
        const headers
            = { Authorization: `Bearer ${token}` };
        try {
            const response = await api.get(`/Categories?pageSize=${pageSize}`, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Create category


export const createCategory = createAsyncThunk(
    'materials/createCategory',
    async (categoryData: { name: string, metadata: any }, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            };
            const response = await api.post('/Categories', categoryData, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Update category 


export const updateCategory = createAsyncThunk(
    'materials/updateCategory',
    async (categoryData: { id: number, name: string, metadata: any, active: boolean }, { rejectWithValue, getState }) => {
        try {
            const token = (getState() as any).auth.token;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            };
            const response = await api.put(`/Categories/${categoryData.id}`, categoryData, { headers });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);



// Define the type for the parameters passed to the thunk
interface FetchDynamicPriceParams {
    width?: number;
    length: number;
    height?: number;
    categoryId: number;
}

// Define the shape of your state (replace `auth` with your actual state structure)
interface State {
    auth: {
        token: string | null;
    };
}

// Define the thunk
export const fetchDynamicPrice = createAsyncThunk<number, FetchDynamicPriceParams, { state: State }>(
    'materials/fetchDynamicPrice',
    async (params, { getState, rejectWithValue }) => {
        const { width, length, height, categoryId } = params;
        const token = getState().auth.token;

        if (!token) {
            return rejectWithValue('No authentication token available');
        }

        // Fetch price for the exact length requested
        const apiParams: Record<string, any> = {
            pageSize: 1,
            page: 1,
            sortOrder: 'DESC',
            categoryId: categoryId,
            length: length,
        };
        if (width) apiParams.width = width;
        if (height) apiParams.height = height;

        try {
            const response = await api.get('/Materials', {
                params: apiParams,
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data.items?.[0]?.defaultPrice || 0;
        } catch (err) {
            console.error(`Error fetching price for params:`, apiParams, err);
            return 0; // Return 0 if the API call fails
        }
    }
);



const initialState: MaterialsState = {
    items: [],
    price: [],
    legPrice: [],
    material: null,
    pagination: {},
    categories: [],
    status: 'idle', // Legacy
    listStatus: 'idle',
    detailsStatus: 'idle',
    saveStatus: 'idle',
    error: null,
};



const materialsSlice = createSlice({
    name: 'materials',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchMaterials.pending, (state) => {
                state.listStatus = 'loading';
                state.status = 'loading';
            })
            .addCase(fetchMaterials.fulfilled, (state, action) => {
                state.listStatus = 'succeeded';
                state.status = 'succeeded';
                // This pulls the array out of the payload object
                state.items = action.payload.items;
            })
            .addCase(fetchMaterials.rejected, (state, action) => {
                state.listStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(fetchMaterialsPrice.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchMaterialsPrice.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.price = action.payload; // action.payload is the entire response.data object
            })
            .addCase(fetchLegsPrice.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.legPrice = action.payload;
            })
            .addCase(fetchMaterialsPrice.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(fetchMaterialById.pending, (state) => {
                state.detailsStatus = 'loading';
                // state.status = 'loading'; // Avoid global spinner
                state.material = null; // Clear the currently selected material
            })
            .addCase(fetchMaterialById.fulfilled, (state, action) => {
                state.detailsStatus = 'succeeded';
                state.status = 'succeeded';
                state.material = action.payload; // Set the selected material
            })
            .addCase(fetchMaterialById.rejected, (state, action) => {
                state.detailsStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(createMaterial.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(createMaterial.fulfilled, (state, action) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
                state.items.push(action.payload);
            })
            .addCase(createMaterial.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(updateMaterial.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(updateMaterial.fulfilled, (state, action) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = action.payload; // Update the material in the list
                }
            })
            .addCase(updateMaterial.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(deleteMaterial.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(deleteMaterial.fulfilled, (state) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
            })
            .addCase(deleteMaterial.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(exportMaterials.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(exportMaterials.fulfilled, (state) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
            })
            .addCase(exportMaterials.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(importMaterials.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(importMaterials.fulfilled, (state) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
            })
            .addCase(importMaterials.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(fetchCategories.pending, (state) => {
                state.listStatus = 'loading';
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.listStatus = 'succeeded';
                state.status = 'succeeded';
                state.categories = action.payload.items;
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.listStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(createCategory.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(createCategory.fulfilled, (state) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(updateCategory.pending, (state) => {
                state.saveStatus = 'loading';
            })
            .addCase(updateCategory.fulfilled, (state) => {
                state.saveStatus = 'succeeded';
                state.status = 'succeeded';
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.saveStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            })
            .addCase(fetchDynamicPrice.pending, (state) => {
                state.detailsStatus = 'loading';
            })
            .addCase(fetchDynamicPrice.fulfilled, (state) => {
                state.detailsStatus = 'succeeded';
                state.status = 'succeeded';
            })
            .addCase(fetchDynamicPrice.rejected, (state, action) => {
                state.detailsStatus = 'failed';
                state.status = 'failed';
                state.error = action.error.message ? { message: action.error.message } : null;
            });


    }
});


export default materialsSlice.reducer;
